/**
 * RelayCarousel — phone-only horizontal pager for relay cards.
 *
 * On a phone the relay cards would otherwise stack into one tall column. This
 * pages them sideways instead: each card or compact relay group snaps to centre
 * with a peek of the neighbouring item on either edge, and a row of pill
 * indicators below shows which item is in view (tap a pill to jump).
 * The peek + the dots make it obvious there's more than one relay to swipe to.
 */
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// The carousel goes full-bleed (a negative margin cancels the page gutter) and
// the scroller re-applies that gutter, so the first card lines up with the page
// content while the peek of the next card runs to the screen edge. The
// dashboard scroll container clips horizontal overflow (overflowX: hidden), so
// the bleed can't spawn a page-level horizontal scrollbar.
const SLIDE_WIDTH = '90%';
const EDGE_PAD = 'var(--page-px)';

export default function RelayCarousel({ items }) {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);
  const ticking = useRef(false);
  const [active, setActive] = useState(0);

  const updateActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slideRefs.current.forEach((slide, i) => {
      if (!slide) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActive(best);
  }, []);

  const onScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      updateActive();
      ticking.current = false;
    });
  }, [updateActive]);

  const goTo = useCallback(i => {
    slideRefs.current[i]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  return (
    <Box sx={{ mt: 1.5, mb: 2, mx: 'calc(-1 * var(--page-px))' }}>
      <Box
        ref={scrollerRef}
        onScroll={onScroll}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: EDGE_PAD,
          // Vertical padding so the cards' top/bottom shadow isn't clipped by
          // the scroll container (overflow-x makes the cross axis non-visible).
          py: 1,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollPaddingInline: EDGE_PAD,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map((item, i) => {
          const isActive = i === active;
          return (
            <Box
              key={item.key}
              ref={el => {
                slideRefs.current[i] = el;
              }}
              sx={{
                flex: `0 0 ${SLIDE_WIDTH}`,
                scrollSnapAlign: 'start',
                minWidth: 0,
                display: 'flex',
                // De-emphasise the peeking neighbours so the edge card reads as
                // "another relay behind", not content cut off at the screen edge.
                opacity: isActive ? 1 : 0.5,
                transform: isActive ? 'none' : 'scale(0.95)',
                transformOrigin: 'center top',
                transition:
                  'opacity var(--duration-slow) var(--easing-standard), transform var(--duration-slow) var(--easing-standard)',
                '& > *': { flex: 1, minWidth: 0 },
              }}
            >
              {item.element}
            </Box>
          );
        })}
      </Box>

      {/* Pill indicators — count + which card is in view; tap to jump. */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        {items.map((item, i) => {
          const isActive = i === active;
          return (
            <Box
              key={item.key}
              component="button"
              type="button"
              onClick={() => goTo(i)}
              aria-label={item.pagerLabel ?? t('sites.relayPagerLabel', { current: i + 1, total: items.length })}
              aria-current={isActive ? 'true' : undefined}
              sx={{
                p: 0.75,
                m: 0,
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                '&:focus-visible': { outline: 'none', '&::before': { boxShadow: 'var(--shadow-focus)' } },
                '&::before': {
                  content: '""',
                  display: 'block',
                  height: 6,
                  width: isActive ? 18 : 6,
                  borderRadius: 3,
                  bgcolor: isActive ? 'primary.main' : 'var(--md-outline-variant)',
                  transition:
                    'width var(--duration-slow) var(--easing-standard), background-color var(--duration-slow) var(--easing-standard)',
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

RelayCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      element: PropTypes.node,
      pagerLabel: PropTypes.string,
    }),
  ).isRequired,
};
